import {
  useDeleteProjectForGroupChatMutation,
  useUpdateProjectForGroupChatMutation,
} from '@crew/apis/project/projectApis'
import { useUploadFileMutation } from '@crew/apis/file/fileApis'
import { Project, ProjectMember } from '@crew/models/domain'
import { useTranslation } from '@crew/modules/i18n'
import { ValidateRules } from '@crew/utils/form'
import { NotifyEventType } from 'enums/app'
import {
  ObjectEventMessage,
  notifyProjectEvent,
  notifyProjectMemberEvent,
} from 'features/app/states/appSlice'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useAppDispatch } from 'states/hooks'

export type FormValues = {
  id: string | null
  subject: string | null
  avatarKey: string | null
}
const formInitialValues: FormValues = {
  id: null,
  subject: null,
  avatarKey: null,
}

export const useGroupChatEditForm = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  // API
  const [
    deleteProjectForGroupChatMutation,
    { isLoading: isLoadingDeleteProjectForGroupChat },
  ] = useDeleteProjectForGroupChatMutation()
  const [
    updateProjectForGroupChatMutation,
    { isLoading: isLoadingUpdateProjectForGroupChat },
  ] = useUpdateProjectForGroupChatMutation()
  const [uploadFileMutation] = useUploadFileMutation()

  // react-hook-formの各種データを取得
  const {
    handleSubmit,
    clearErrors,
    formState,
    reset,
    control,
    setError,
    setValue,
    getValues,
    watch,
  } = useForm<FormValues>({
    criteriaMode: 'all',
    defaultValues: formInitialValues,
  })

  // バリデーションルール
  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      subject: {
        required: t('message.general.required'),
        maxLength: {
          value: 100,
          message: t('message.general.maxLength', {
            name: t('label.subject'),
            value: 100,
          }),
        },
      },
      id: {},
      avatarKey: {},
    }),
    [t]
  )

  // グループチャット削除処理
  const deleteGroupChat = useCallback(
    async (projectId: string, version: number) => {
      await deleteProjectForGroupChatMutation({
        projectId,
        version,
      }).unwrap()

      const objectEventMessage: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Deleted,
        id: projectId,
        object: undefined,
      }
      dispatch(notifyProjectEvent(objectEventMessage))
    },
    [deleteProjectForGroupChatMutation, dispatch]
  )

  // グループチャット更新処理
  const updateGroupChat = useCallback(
    async (projectId: string, version: number, data: FormValues) => {
      const result = await updateProjectForGroupChatMutation({
        project: {
          id: projectId,
          subject: data.subject as string,
          avatarKey: data.avatarKey ?? undefined,
          version: version,
        },
      }).unwrap()

      const objectEventMessage: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Updated,
        id: result.project.id,
        object: result.project,
      }
      dispatch(notifyProjectEvent(objectEventMessage))
      const objectEventMessageProjectMember: ObjectEventMessage<ProjectMember> =
        {
          eventType: NotifyEventType.Updated,
          id: result.project.id,
          object: undefined,
        }
      dispatch(notifyProjectMemberEvent(objectEventMessageProjectMember))
      return result
    },
    [dispatch, updateProjectForGroupChatMutation]
  )

  // ファイルアップロード処理
  const uploadFile = useCallback(
    async (file: File) => {
      const result = await uploadFileMutation({ file }).unwrap()

      return result
    },
    [uploadFileMutation]
  )

  // set cover base64
  const convertBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)

      fileReader.onload = () => {
        resolve(fileReader.result)
      }

      fileReader.onerror = (error) => {
        reject(error)
      }
    })
  }

  return {
    handleSubmit,
    clearErrors,
    formState,
    reset,
    control,
    setError,
    setValue,
    getValues,
    watch,
    validateRules,

    deleteGroupChat,
    updateGroupChat,
    isLoadingDeleteProjectForGroupChat,
    isLoadingUpdateProjectForGroupChat,

    uploadFile,
    convertBase64,
  }
}
